import React, { useState, useEffect, useCallback } from 'react';
import { db, getCollectionName } from './firebase';
import { doc, getDoc, setDoc } from './firestore-compat';
import { speakStop } from './utils/speechUtils';

// ── Eventos configuráveis ────────────────────────────────────────────────────
const EVENTS = [
  {
    key: 'novo_paciente',
    label: 'Novo Paciente Cadastrado',
    icon: '📥',
    desc: 'Disparado quando um novo paciente entra na esteira',
    vars: ['{convenio}', '{paciente}', '{tempo}'],
    default: 'Operadora {convenio} solicitou avaliação do paciente {paciente}, restam {tempo} para vencer o SLA',
  },
  {
    key: 'visita_agendada',
    label: 'Visita Agendada',
    icon: '📅',
    desc: 'Disparado quando o ADM agenda a visita de avaliação',
    vars: ['{paciente}', '{profissional}'],
    default: 'Avaliação de {paciente} agendada para {profissional}',
  },
  {
    key: 'status_change',
    label: 'Mudança de Status',
    icon: '🔄',
    desc: 'Disparado em qualquer mudança de estágio',
    vars: ['{paciente}', '{status}'],
    default: 'Paciente {paciente} entrou em {status}',
  },
  {
    key: 'status_change_sla',
    label: 'Mudança de Status com SLA',
    icon: '🔄⏱',
    desc: 'Usado enquanto o SLA está ativo — informa estágio e tempo restante',
    vars: ['{paciente}', '{status}', '{tempo}'],
    default: 'Paciente {paciente} entrou em {status}, restam {tempo} para vencer o SLA',
  },
  {
    key: 'avaliacao_confirmada',
    label: 'Avaliação Confirmada',
    icon: '✅',
    desc: 'Disparado quando a profissional confirma a visita',
    vars: ['{paciente}', '{profissional}'],
    default: 'Avaliação de {paciente} foi confirmada por {profissional}',
  },
  {
    key: 'sla_alerta',
    label: 'Alerta de SLA (próximo de vencer)',
    icon: '⚠️',
    desc: 'Disparado quando faltam poucas horas para o SLA vencer',
    vars: ['{paciente}', '{tempo}'],
    default: 'Atenção, faltam {tempo} para vencer o SLA do Paciente {paciente}',
  },
  {
    key: 'sla_vencido',
    label: 'SLA Vencido',
    icon: '🚨',
    desc: 'Disparado quando o SLA de um paciente já venceu',
    vars: ['{paciente}', '{tempo}'],
    default: 'SLA do Paciente {paciente} vencido há {tempo}',
  },
  {
    key: 'implantado',
    label: 'Paciente Implantado',
    icon: '🎉',
    desc: 'Disparado quando o paciente chega ao status final',
    vars: ['{paciente}'],
    default: 'Parabéns! Paciente {paciente} foi implantado com sucesso',
  },
];

const DEFAULT_CONFIG = {
  rate: 0.88,
  pitch: 1.05,
  volume: 1,
  ativo: true,
  templates: Object.fromEntries(EVENTS.map(e => [e.key, e.default])),
};

// ── Design tokens ─────────────────────────────────────────────────────────────
const C  = { border:'#e2e8f0', bg:'#f8fafc', white:'#fff', text:'#1e293b', muted:'#64748b', primary:'#6366f1' };
const lbl = { display:'block', fontSize:'0.72rem', fontWeight:700, color:C.muted, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' };

// ── Preview de voz com configurações ─────────────────────────────────────────
const previewSpeak = (text, rate, pitch, volume, voiceName) => {
  if (!window.speechSynthesis) { alert('Seu browser não suporta síntese de voz.'); return; }
  // speakStop resets the queue's busy flag before cancelling, preventing permanent queue jam
  speakStop();
  const u = new SpeechSynthesisUtterance(text);
  u.lang   = 'pt-BR';
  u.rate   = parseFloat(rate);
  u.pitch  = parseFloat(pitch);
  u.volume = parseFloat(volume);
  const voices = window.speechSynthesis.getVoices();
  const preferred = voiceName ? voices.find(v => v.name === voiceName) : null;
  const ptVoice = preferred || voices.find(v => v.lang === 'pt-BR') || voices.find(v => v.lang.startsWith('pt'));
  if (ptVoice) u.voice = ptVoice;
  window.speechSynthesis.speak(u);
};

// ── Slider com label ──────────────────────────────────────────────────────────
function LabeledSlider({ label, value, min, max, step, onChange, leftLabel, rightLabel, displayValue }) {
  return (
    <div style={{ marginBottom:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
        <label style={lbl}>{label}</label>
        <span style={{ fontSize:'0.82rem', fontWeight:800, color:C.primary, background:`${C.primary}15`, padding:'2px 10px', borderRadius:20 }}>{displayValue || value}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(e.target.value)}
        style={{ width:'100%', accentColor:C.primary, height:6, cursor:'pointer' }} />
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.68rem', color:C.muted, marginTop:4 }}>
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SpeechConfig() {
  const [tab,       setTab]       = useState('parametros'); // 'parametros' | 'textos'
  const [config,    setConfig]    = useState(DEFAULT_CONFIG);
  const [templates, setTemplates] = useState(DEFAULT_CONFIG.templates);
  const [voices,    setVoices]    = useState([]);
  const [selVoice,  setSelVoice]  = useState('');
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loading,   setLoading]   = useState(true);

  // Carrega configurações do Firestore
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, getCollectionName('configuracoes_v2'), 'speech'));
        if (snap.exists()) {
          const data = snap.data();
          setConfig(prev => ({ ...DEFAULT_CONFIG, ...data }));
          setTemplates(prev => ({ ...DEFAULT_CONFIG.templates, ...(data.templates || {}) }));
          if (data.voiceName) setSelVoice(data.voiceName);
        }
      } catch(e) { console.warn('SpeechConfig load:', e.message); }
      setLoading(false);
    };
    load();
  }, []);

  // Carrega vozes disponíveis
  useEffect(() => {
    const loadVoices = () => {
      const v = window.speechSynthesis?.getVoices() || [];
      setVoices(v);
      const ptVoice = v.find(x => x.lang === 'pt-BR') || v.find(x => x.lang.startsWith('pt'));
      if (ptVoice && !selVoice) setSelVoice(ptVoice.name);
    };
    loadVoices();
    if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = loadVoices;
  }, [selVoice]);

  const setParam = (key, val) => setConfig(p => ({ ...p, [key]: parseFloat(val) }));

  const setTemplate = (key, val) => setTemplates(p => ({ ...p, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const toSave = {
        ...config,
        templates,
        voiceName: selVoice,
        updatedAt: new Date().toISOString(),
      };
      await setDoc(doc(db, getCollectionName('configuracoes_v2'), 'speech'), toSave);
      // Atualiza em runtime sem precisar recarregar
      window.__speechConfig = toSave;
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error('SpeechConfig save error:', e);
      alert(`Erro ao salvar configurações de voz:\n${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const previewText = (tpl) => {
    const sample = tpl
      .replace('{convenio}', 'Unimed')
      .replace('{paciente}', 'João Silva')
      .replace('{profissional}', 'Thamyris')
      .replace('{status}', 'Sessão Clínica')
      .replace('{tempo}', 'duas horas');
    previewSpeak(sample, config.rate, config.pitch, config.volume, selVoice);
  };

  const insertVar = (key, varName) => {
    setTemplates(p => ({ ...p, [key]: (p[key] || '') + varName }));
  };

  if (loading) return <div style={{ padding:40, textAlign:'center', color:C.muted }}>Carregando configurações...</div>;

  const rateLabel = config.rate < 0.7 ? 'Muito lento' : config.rate < 0.9 ? 'Lento' : config.rate < 1.1 ? 'Normal' : config.rate < 1.3 ? 'Rápido' : 'Muito rápido';
  const pitchLabel = config.pitch < 0.7 ? 'Muito grave' : config.pitch < 0.9 ? 'Grave' : config.pitch < 1.1 ? 'Normal' : config.pitch < 1.3 ? 'Agudo' : 'Muito agudo';

  return (
    <div style={{ padding:24, maxWidth:900 }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h3 style={{ margin:'0 0 4px', fontWeight:800, fontSize:'1.1rem', color:C.text }}>🔊 Configurações de Voz</h3>
          <p style={{ margin:0, fontSize:'0.82rem', color:C.muted }}>Personalize o texto, velocidade e tom dos avisos sonoros do sistema</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {/* Toggle ativo/inativo */}
          <div style={{ display:'flex', alignItems:'center', gap:8, background:C.bg, borderRadius:10, padding:'8px 14px', border:`1px solid ${C.border}` }}>
            <span style={{ fontSize:'0.8rem', fontWeight:600, color:C.muted }}>Avisos de voz</span>
            <button
              onClick={() => setConfig(p => ({ ...p, ativo: !p.ativo }))}
              style={{ width:44, height:24, borderRadius:12, border:'none', cursor:'pointer', background:config.ativo?C.primary:'#cbd5e1', position:'relative', transition:'background 0.2s' }}>
              <span style={{ position:'absolute', top:3, left:config.ativo?22:3, width:18, height:18, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
            </button>
            <span style={{ fontSize:'0.78rem', fontWeight:700, color:config.ativo?C.primary:C.muted }}>{config.ativo?'Ativo':'Inativo'}</span>
          </div>
          <button onClick={handleSave} disabled={saving}
            style={{ background:saved?'#22c55e':C.primary, color:'#fff', border:'none', borderRadius:8, padding:'9px 20px', cursor:'pointer', fontWeight:700, fontSize:'0.84rem', opacity:saving?0.7:1, transition:'background 0.3s' }}>
            {saving?'⏳ Salvando...':saved?'✅ Salvo!':'💾 Salvar'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`, marginBottom:24 }}>
        {[{ k:'parametros', l:'⚙️ Parâmetros de Voz' }, { k:'textos', l:'📝 Textos dos Avisos' }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{ border:'none', background:'none', padding:'10px 20px', cursor:'pointer', fontWeight:tab===t.k?700:500, fontSize:'0.88rem', color:tab===t.k?C.primary:C.muted, borderBottom:tab===t.k?`2px solid ${C.primary}`:'2px solid transparent', marginBottom:-1 }}>
            {t.l}
          </button>
        ))}
      </div>

      {/* ── Parâmetros de voz ── */}
      {tab === 'parametros' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, alignItems:'start' }}>
          {/* Sliders */}
          <div style={{ background:C.white, borderRadius:14, padding:24, border:`1px solid ${C.border}`, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <h4 style={{ margin:'0 0 20px', fontSize:'0.9rem', fontWeight:700, color:C.text }}>Ajustes de Voz</h4>

            <LabeledSlider
              label="Velocidade" value={config.rate} min={0.5} max={1.5} step={0.05}
              onChange={v => setParam('rate', v)}
              leftLabel="0.5× Lento" rightLabel="1.5× Rápido"
              displayValue={`${config.rate}× — ${rateLabel}`}
            />

            <LabeledSlider
              label="Tom (Pitch)" value={config.pitch} min={0.5} max={1.5} step={0.05}
              onChange={v => setParam('pitch', v)}
              leftLabel="Grave" rightLabel="Agudo"
              displayValue={`${config.pitch} — ${pitchLabel}`}
            />

            <LabeledSlider
              label="Volume" value={config.volume} min={0} max={1} step={0.05}
              onChange={v => setParam('volume', v)}
              leftLabel="Mudo" rightLabel="Máximo"
              displayValue={`${Math.round(config.volume * 100)}%`}
            />

            {/* Seletor de voz */}
            {voices.filter(v => v.lang.startsWith('pt')).length > 0 && (
              <div>
                <label style={lbl}>Voz (pt-BR disponíveis)</label>
                <select
                  value={selVoice}
                  onChange={e => setSelVoice(e.target.value)}
                  style={{ border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 12px', fontSize:'0.84rem', width:'100%', outline:'none', color:C.text }}>
                  {voices.filter(v => v.lang.startsWith('pt')).map(v => (
                    <option key={v.name} value={v.name}>{v.name} ({v.lang})</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Preview */}
          <div style={{ background:C.white, borderRadius:14, padding:24, border:`1px solid ${C.border}`, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <h4 style={{ margin:'0 0 16px', fontSize:'0.9rem', fontWeight:700, color:C.text }}>🎧 Teste a Configuração</h4>
            <p style={{ fontSize:'0.82rem', color:C.muted, marginBottom:16, lineHeight:1.5 }}>
              Clique nos botões abaixo para ouvir como cada evento vai soar com os parâmetros configurados.
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {EVENTS.map(e => (
                <button key={e.key}
                  onClick={() => previewText(templates[e.key] || e.default)}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:C.bg, border:`1px solid ${C.border}`, borderRadius:10, cursor:'pointer', textAlign:'left', transition:'all 0.15s' }}
                  onMouseEnter={el => { el.currentTarget.style.background='#eef2ff'; el.currentTarget.style.borderColor=C.primary; }}
                  onMouseLeave={el => { el.currentTarget.style.background=C.bg; el.currentTarget.style.borderColor=C.border; }}>
                  <span style={{ fontSize:'1.1rem' }}>{e.icon}</span>
                  <div>
                    <div style={{ fontSize:'0.82rem', fontWeight:700, color:C.text }}>{e.label}</div>
                    <div style={{ fontSize:'0.7rem', color:C.muted }}>{(templates[e.key]||e.default).substring(0,50)}...</div>
                  </div>
                  <span style={{ marginLeft:'auto', color:C.primary, fontSize:'1rem' }}>▶️</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => window.speechSynthesis?.cancel()}
              style={{ marginTop:12, width:'100%', padding:'8px', background:'#fee2e2', border:'1px solid #fecaca', borderRadius:8, cursor:'pointer', color:'#ef4444', fontWeight:700, fontSize:'0.82rem' }}>
              ⏹ Parar
            </button>
          </div>
        </div>
      )}

      {/* ── Textos dos avisos ── */}
      {tab === 'textos' && (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* Info sobre variáveis */}
          <div style={{ background:'#fffbeb', borderRadius:10, padding:'12px 16px', border:'1px solid #fde68a', fontSize:'0.8rem', color:'#92400e' }}>
            💡 <strong>Variáveis:</strong> use <code style={{ background:'#fff3cd', padding:'1px 5px', borderRadius:4 }}>{'{variavel}'}</code> no texto — o sistema substitui automaticamente pelo valor real. Clique na variável para inserir no campo.
          </div>

          {EVENTS.map(e => (
            <div key={e.key} style={{ background:C.white, borderRadius:14, padding:20, border:`1px solid ${C.border}`, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
                <div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                    <span style={{ fontSize:'1.1rem' }}>{e.icon}</span>
                    <span style={{ fontWeight:700, fontSize:'0.9rem', color:C.text }}>{e.label}</span>
                  </div>
                  <span style={{ fontSize:'0.75rem', color:C.muted }}>{e.desc}</span>
                </div>
                <button
                  onClick={() => previewText(templates[e.key] || e.default)}
                  style={{ background:`${C.primary}15`, color:C.primary, border:`1px solid ${C.primary}30`, borderRadius:8, padding:'5px 12px', cursor:'pointer', fontWeight:700, fontSize:'0.75rem', whiteSpace:'nowrap' }}>
                  ▶️ Ouvir
                </button>
              </div>

              {/* Variáveis disponíveis */}
              <div style={{ display:'flex', gap:6, marginBottom:10, flexWrap:'wrap' }}>
                <span style={{ fontSize:'0.7rem', color:C.muted, fontWeight:600, alignSelf:'center' }}>Variáveis:</span>
                {e.vars.map(v => (
                  <button key={v} onClick={() => insertVar(e.key, v)}
                    style={{ background:`${C.primary}10`, color:C.primary, border:`1px solid ${C.primary}25`, borderRadius:6, padding:'2px 10px', cursor:'pointer', fontWeight:700, fontSize:'0.75rem', fontFamily:'monospace' }}>
                    {v}
                  </button>
                ))}
                <button
                  onClick={() => setTemplates(p => ({ ...p, [e.key]: e.default }))}
                  style={{ marginLeft:'auto', background:'none', border:`1px solid ${C.border}`, borderRadius:6, padding:'2px 10px', cursor:'pointer', color:C.muted, fontSize:'0.72rem' }}>
                  ↩ Padrão
                </button>
              </div>

              {/* Input do template */}
              <textarea
                value={templates[e.key] || e.default}
                onChange={ev => setTemplate(e.key, ev.target.value)}
                rows={2}
                style={{ border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px', fontSize:'0.88rem', width:'100%', boxSizing:'border-box', outline:'none', color:C.text, resize:'vertical', fontFamily:'inherit', lineHeight:1.5 }}
              />
              {/* Preview com variáveis substituídas */}
              <div style={{ marginTop:6, fontSize:'0.75rem', color:C.muted, background:C.bg, borderRadius:6, padding:'6px 10px' }}>
                <strong>Preview:</strong>{' '}
                <em style={{ color:'#475569' }}>
                  {(templates[e.key] || e.default)
                    .replace('{convenio}', 'Unimed')
                    .replace('{paciente}', 'João Silva')
                    .replace('{profissional}', 'Thamyris')
                    .replace('{status}', 'Sessão Clínica')
                    .replace('{tempo}', '2h 30m')}
                </em>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
